// src/hooks/useFeatureFlag.js
import { useCallback, useEffect, useRef, useState } from "react";
import { initialize } from "one-xp-sdk";
import { fetchFeatureFlag } from "./fetchFeatureFlag";

import { getOneXpEndpoints } from "./getOneXpEndpoints";

const createSdkClient = (oneXpBaseUrl, oneXpFaasUrl) =>
  initialize({
    oneXPBaseURL: oneXpBaseUrl,
    oneXPFaasURL: oneXpFaasUrl,
  });


export function useFeatureFlag(featureFlagName) {
  const sdkRef = useRef(null);

  const [state, setState] = useState({
    enabled: false,
    properties: {},
    loading: true,
    error: null,
  });

  const fetchFlag = useCallback(
    async (signal) => {
      try {
        setState((s) => ({ ...s, loading: true, error: null }));
        // Ensure SDK exists before calling findVariants
        if (!sdkRef.current) {
          throw new Error("OneXP SDK not initialized");
        }

        const experiments = await sdkRef.current.findVariants({
          locator: window.location.href,
          fetchOpts: { credentials: "include" },
        });

        const featureFlags = (experiments || []).filter(
          ({ type }) => type === "FEATURE"
        );

        const { enabled, properties } = fetchFeatureFlag(
          featureFlags,
          featureFlagName
        );

        setState({ enabled, properties, loading: false, error: null });
      } catch (error) {
        if (signal && signal.aborted) return;
        setState((s) => ({ ...s, loading: false, error }));
      }
    },
    [featureFlagName]
  );

  useEffect(() => {
    const controller = new AbortController();

    const initializeSdkAndFetchFlag = async () => {
      if (controller.signal.aborted) return;
      try {
        const env = await getOneXpEndpoints();
        if (controller.signal.aborted) return;
        sdkRef.current = createSdkClient(
          env.ONE_XP_BASE_URL || "",
          env.ONE_XP_FAAS_URL || ""
        );
        fetchFlag(controller.signal);
      } catch (err) {
        if (controller.signal.aborted) return;
        setState(s => ({ ...s, loading: false, error: err }));
      }
    };
    initializeSdkAndFetchFlag();

    return () => controller.abort();
  }, [fetchFlag]);

  return {
    enabled: state.enabled,
    properties: state.properties,
    loading: state.loading,
    error: state.error,
  };
}
